/**
 * A Support at home plan in everyday words — the card title, the line under it, and what the means
 * tests do with it (Support at home rebuild, 19 Oct 2026; the review's vocabulary table:
 * "Increase home equity ($250,000)" → "Spend $250,000 on the home", "Purchase $250,000 Annuity (nil
 * residual)" → "Buy a $250,000 lifetime income for Colin").
 *
 * Read from the engine candidate's own config — the same fields the evaluator applied — so the words
 * can never describe a different plan from the figures beside them. Accounts are named the way the
 * client would say them: a couple's two "MLC" accounts are "Colin's MLC" and "Shelly's MLC".
 */
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { householdVoice } from '../../utils/householdWords.ts';

export interface SahConfigLike {
    type?: string;
    name?: string;
    amount?: number;
    perPersonAmount?: number;
    purchaseAmount?: number;
    residualCapital?: number;
    annuityType?: string;
    ownership?: string;
    equityAmount?: number;
    renovationAmount?: number;
    newHomeValue?: number;
    downsizeDetails?: { useDownsizer?: boolean; commenceIncomeStream?: boolean; repayDebt?: boolean; debtRepayment?: number; selectedDebtKey?: string } | null;
    repaymentAmount?: number;
    liabilityDescription?: string;
    loanAmount?: number;
    paymentAmount?: number;
    fundingSources?: Array<{ type?: string; id?: string | number; description?: string; amountToTake?: number }>;
    steps?: SahConfigLike[];
    investmentPropertyDetails?: unknown;
    [key: string]: unknown;
}

export interface SahPlanWords {
    title: string;
    /** What it is paid from, and what the means tests do with it. */
    sub: string;
    /** One line for each piece: what the means tests do with it (for Explain this and the risks). */
    treatment: string[];
}

const money = (n: number | undefined) => `$${Math.round(Number(n) || 0).toLocaleString('en-AU')}`;

const KIND_WORD: Record<string, string> = {
    bankAccounts: 'account',
    accountBasedPensions: 'pension',
    shares: 'shares',
    termDeposits: 'term deposit',
    managedFunds: 'managed fund',
};

/** "the CBA account", "Colin's MLC pension". */
export function fundingAccountName(source: { type?: string; id?: string | number; description?: string }, factFind: DraftFactFind | null | undefined): string {
    const list = ((factFind?.assets as Record<string, Array<{ id?: unknown; description?: string; ownership?: string }>> | undefined)?.[String(source.type)] || []);
    const asset = list.find((a) => String(a?.id) === String(source.id));
    const desc = String(source.description || asset?.description || '').trim() || 'savings';
    const kind = KIND_WORD[String(source.type)] || '';
    // "CBA Acc" is already an account; don't make it "CBA Acc account".
    const alreadySaid = kind === 'account' ? /\bacc(ount|t)?\b/i.test(desc) : desc.toLowerCase().includes(kind);
    const withKind = kind && !alreadySaid ? `${desc} ${kind}` : desc;
    const isCouple = factFind?.adviceFor === 'couple';
    const owner = isCouple && asset?.ownership === 'client' ? factFind?.client?.firstName
        : isCouple && asset?.ownership === 'partner' ? factFind?.partner?.firstName
            : null;
    if (owner && !withKind.toLowerCase().includes(String(owner).toLowerCase())) return `${owner}'s ${withKind}`;
    return `the ${withKind}`;
}

/** "Paid from $150,000 of the CBA account and $100,000 of Colin's MLC pension." */
export function fundingSentence(config: SahConfigLike, factFind: DraftFactFind | null | undefined): string {
    const parts = (config.fundingSources || [])
        .filter((s) => Number(s.amountToTake) > 0)
        .map((s) => `${money(s.amountToTake)} of ${fundingAccountName(s, factFind)}`);
    if (!parts.length) return '';
    const joined = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
    return `Paid from ${joined}.`;
}

const firstName = (factFind: DraftFactFind | null | undefined, ownership: unknown): string => {
    if (ownership === 'partner' && factFind?.partner?.firstName) return String(factFind.partner.firstName);
    return String(factFind?.client?.firstName || 'you');
};

/** One piece's title, lower-cased after the first so pieces can be joined into one sentence. */
function pieceTitle(c: SahConfigLike, factFind: DraftFactFind | null | undefined): string {
    const isCouple = factFind?.adviceFor === 'couple';
    switch (c.type) {
        case 'gifting':
            return `Give ${money(c.amount)} to family`;
        case 'funeralBonds':
            return isCouple ? `Prepay both funerals with ${money(c.amount)} of funeral bonds` : `Prepay the funeral with a ${money(c.amount)} funeral bond`;
        case 'annuity': {
            const who = firstName(factFind, c.ownership);
            return `Buy a ${money(c.purchaseAmount)} lifetime income for ${who}`;
        }
        case 'upgrade':
            // A plan built by hand names the dearer home it moves to.
            if (c.newHomeValue != null && c.equityAmount == null) return `Sell and buy a dearer ${money(c.newHomeValue)} home`;
            return `Spend ${money(c.equityAmount ?? c.renovationAmount)} on the home`;
        case 'increaseHomeEquity':
        case 'renovate':
            return `Spend ${money(c.equityAmount ?? c.renovationAmount)} on the home`;
        case 'downsize': {
            const d = c.downsizeDetails || {};
            const base = `Sell and buy a ${money(c.newHomeValue)} home`;
            if (d.repayDebt) return `${base}, and pay off a debt`;
            if (d.useDownsizer && d.commenceIncomeStream) return `${base}, and start a pension with the downsizer money`;
            if (d.useDownsizer) return `${base}, and put the downsizer amount into super`;
            return base;
        }
        case 'debtRepayment': {
            const what = String(c.liabilityDescription || 'the debt').trim();
            const full = !/partial/i.test(String(c.name || ''));
            return full ? `Pay off the ${what}` : `Pay half of the ${what}`;
        }
        case 'reverseMortgage':
            return `Borrow ${money(c.loanAmount)} against the home`;
        case 'heas':
            return `Take ${money(c.paymentAmount)} a ${c.paymentFrequency === 'monthly' ? 'month' : 'fortnight'} from the Home Equity Access Scheme`;
        case 'sellEntityInvestmentProperty':
            return String(c.name || 'Sell the investment property').replace(/\s*\(entity pool\)\s*$/i, '');
        default:
            return String(c.name || 'This plan');
    }
}

function pieceTreatment(c: SahConfigLike): string | null {
    switch (c.type) {
        case 'gifting': return 'Gifts within the yearly free limits stop being counted; anything above them still counts for five years.';
        case 'funeralBonds': return 'A funeral bond up to the allowed amount is not counted in either means test.';
        // The assessment in full, and what comes back (owner, 20 Oct 2026: "60% for the first years" was too loose).
        case 'annuity': {
            const counted = 'Centrelink counts 60% of the price for the first five years, or until age 85 if that is later, and 30% after that, and 60% of each payment as income';
            if (/guaranteed/i.test(String(c.name || ''))) return `${counted}; the full price comes back to the estate whenever death comes.`;
            return c.residualCapital === 1
                ? `${counted}; the full price comes back to the estate for the first half of the life expectancy period, then a falling amount.`
                : `${counted}; nothing comes back to the estate.`;
        }
        case 'increaseHomeEquity': case 'renovate': case 'upgrade':
            // Saved with the chosen plan and read in either voice, so it names nobody (owner, 21 Oct 2026).
            return 'The home lived in is left out of both means tests, so the money spent on it stops being counted.';
        case 'downsize': return 'The money left after buying the new home is counted in full, unless it goes into super or a pension.';
        case 'debtRepayment': return 'Less cash is counted, and the repayments and interest stop.';
        case 'reverseMortgage': return 'The loan money is counted while it sits in the bank; the loan and its interest are repaid from the home when it is sold.';
        case 'heas': return 'The payments are not counted as income; the loan and its interest are repaid from the home when it is sold.';
        default: return null;
    }
}

/**
 * The pieces a plan is made of, as the plan-piece kinds Explain this explains (owner, 24 Oct 2026: "it doesnt
 * actually explain what it is, how it works"). Read from the plan's own config, step by step for a combination,
 * so "Spend $416,000 on the home and pay off the credit card" explains both.
 */
export function sahLeverKinds(config: SahConfigLike | null | undefined): string[] {
    const c = config || {};
    const steps = c.type === 'combination' && Array.isArray(c.steps) ? c.steps : [c];
    const kinds: string[] = [];
    for (const s of steps) {
        switch (s.type) {
            case 'gifting': kinds.push('gift'); break;
            case 'funeralBonds': kinds.push('funeralBond'); break;
            case 'annuity': {
                const name = String(s.name || '').toLowerCase();
                kinds.push(s.annuityType === 'guaranteed' || name.includes('guaranteed') ? 'annuityGuaranteed' : (Number(s.residualCapital) || 0) > 0 || name.includes('capital returned') ? 'annuityReturned' : 'annuityNil');
                break;
            }
            case 'increaseHomeEquity': case 'renovate': case 'upgrade': kinds.push('renovate'); break;
            case 'downsize': {
                const d = s.downsizeDetails || {};
                kinds.push('sellHome');
                if (d.useDownsizer) kinds.push('superContribution');
                if (d.useDownsizer && d.commenceIncomeStream) kinds.push('accountBasedPension');
                if (d.repayDebt) kinds.push('clearDebt');
                break;
            }
            case 'debtRepayment': kinds.push('clearDebt'); break;
            case 'reverseMortgage': kinds.push('reverseMortgage'); break;
            case 'heas': kinds.push('heas'); break;
            case 'sellEntityInvestmentProperty': kinds.push('sellInvestmentProperty'); break;
            default: break;
        }
    }
    return kinds;
}

export function sahPlanWords(config: SahConfigLike | null | undefined, factFind: DraftFactFind | null | undefined, fallbackName?: string): SahPlanWords {
    const c = config || {};
    if (c.type === 'combination' && Array.isArray(c.steps) && c.steps.length > 0) {
        const titles = c.steps.map((s) => pieceTitle(s, factFind));
        const title = titles.map((t, i) => (i === 0 ? t : t.charAt(0).toLowerCase() + t.slice(1))).join(titles.length > 2 ? ', ' : ' and ');
        const finalTitle = titles.length > 2 ? title.replace(/, ([^,]*)$/, ', and $1') : title;
        const funding = c.steps.map((s) => fundingSentence(s, factFind)).filter(Boolean)[0] || '';
        const treatment = c.steps.map(pieceTreatment).filter((t): t is string => Boolean(t));
        return { title: finalTitle, sub: funding, treatment };
    }
    if (!c.type || c.type === 'doNothing') {
        return { title: fallbackName || 'Keep things as they are', sub: '', treatment: [] };
    }
    const treatment = pieceTreatment(c);
    const funding = fundingSentence(c, factFind);
    const extra = c.type === 'annuity'
        ? (c.annuityType === 'guaranteed'
            ? `The full amount comes back to the estate whenever ${firstName(factFind, c.ownership)} dies.`
            : c.residualCapital === 1
                ? `Money comes back to the estate on an early death.`
                : `It pays while ${firstName(factFind, c.ownership)} lives; nothing comes back.`)
        : '';
    return {
        title: pieceTitle(c, factFind),
        sub: [funding, extra].filter(Boolean).join(' '),
        treatment: treatment ? [treatment] : [],
    };
}

/* ── What happens to a pension account the plan draws on ──────────────────────────────────────── */

/** One pension account the plan takes money from, and its yearly draw before and after. */
export interface AbpDrawChange {
    name: string;
    taken: number;
    before: number;
    after: number;
    /** The account is emptied by the plan. */
    closed: boolean;
}

/** From an engine row's `assetAllocation` (the server adds drawBefore / drawAfter for pensions). */
export function abpDrawChangesFromAllocation(allocation: unknown, factFind: DraftFactFind | null | undefined): AbpDrawChange[] {
    const rows = Array.isArray(allocation) ? allocation as Array<Record<string, unknown>> : [];
    return rows
        .filter((r) => r.assetType === 'accountBasedPensions' && Number(r.liquidatedAmount) > 0 && r.drawBefore != null)
        .map((r) => ({
            name: fundingAccountName({ type: 'accountBasedPensions', id: r.id as string | number | undefined, description: String(r.assetName || '') }, factFind),
            taken: Number(r.liquidatedAmount) || 0,
            before: Number(r.drawBefore) || 0,
            after: Number(r.drawAfter) || 0,
            closed: Number(r.remainingAmount) <= 0,
        }));
}

/** Between the profile and the position with a hand-built plan applied (sahPlanFactFind). */
export function abpDrawChangesBetween(original: DraftFactFind | null | undefined, planned: DraftFactFind | null | undefined): AbpDrawChange[] {
    const after = new Map(((planned?.assets?.accountBasedPensions || []) as Array<{ id?: unknown; value?: unknown; income?: unknown }>).map((a) => [String(a.id), a]));
    return ((original?.assets?.accountBasedPensions || []) as Array<{ id?: unknown; value?: unknown; income?: unknown; description?: string }>)
        .map((a) => {
            const now = after.get(String(a.id));
            const was = Number(a.value) || 0;
            const left = now ? Number(now.value) || 0 : 0;
            return {
                name: fundingAccountName({ type: 'accountBasedPensions', id: a.id as string | number | undefined, description: a.description }, original),
                taken: was - left,
                before: Number(a.income) || 0,
                after: now ? Number(now.income) || 0 : 0,
                closed: left <= 0,
            };
        })
        .filter((c) => c.taken >= 1);
}

/**
 * The plain-words line for each pension account the plan draws on (owner, 19 Oct 2026: keep the rule,
 * "the explainer should clearly outline what the system's done so it's clear to the user"). The rule:
 * once money is taken out, the account pays only the legal minimum each year; any year the income falls
 * short, the gap comes from savings in the settlement order (pack B.SU, B.PE.6).
 */
const SHORTFALL_ORDER = 'Any year the income then falls short, the gap is taken from savings in a set order: the bank accounts first, then the pension accounts, then super, then shares.';

/** One line of Explain this: a bold lead, then what it means. */
export interface AbpDrawLine { lead: string; body: string }

export function abpDrawSentences(changes: AbpDrawChange[]): AbpDrawLine[] {
    return changes.map((c) => {
        const name = c.name.charAt(0).toUpperCase() + c.name.slice(1);
        if (c.closed) return { lead: `${name} is used up by the plan,`, body: `so the ${money(c.before)} a year it paid stops. ${SHORTFALL_ORDER}` };
        if (c.after >= c.before - 1) return { lead: `${name} keeps paying the legal minimum,`, body: `which on the balance left after ${money(c.taken)} comes out is ${money(c.after)} a year (it was ${money(c.before)}). ${SHORTFALL_ORDER}` };
        return { lead: `${name} drops to the legal minimum:`, body: `${money(c.taken)} comes out of it, so from then on it pays ${money(c.after)} a year instead of ${money(c.before)}. ${SHORTFALL_ORDER}` };
    });
}

/** "you" for the client, "Colin and Shelly" for an adviser — who the plans are for (householdVoice). */
export const sahHousehold = (factFind: DraftFactFind | null | undefined): string => householdVoice(factFind).names;
